import {
  createContext,
  useContext,
  useEffect,
  useState,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { doc, getDoc, onSnapshot } from "firebase/firestore";
import { auth, db, functionsEurope as functions } from "../services/firebase";
import { httpsCallable } from "firebase/functions";

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(null);
  const [needsSubscription, setNeedsSubscription] = useState(false);
  const [accessInfo, setAccessInfo] = useState(null);
  const accessRefreshPromiseRef = useRef(null);

  const sleep = useCallback((ms) => new Promise((resolve) => setTimeout(resolve, ms)), []);

  const refreshAccess = useCallback(() => {
    if (accessRefreshPromiseRef.current) {
      return accessRefreshPromiseRef.current;
    }

    accessRefreshPromiseRef.current = (async () => {
      try {
        const checkAccess = httpsCallable(functions, "get_my_access_status");
        const accessResult = await checkAccess({});
        const access = accessResult?.data || {};
        setAccessInfo(access);
        const granted = access?.success && access?.canAccess === true;
        setNeedsSubscription(!granted);
        return granted;
      } catch (err) {
        console.error("refreshAccess failed:", err);
        return false;
      }
    })().finally(() => {
      accessRefreshPromiseRef.current = null;
    });

    return accessRefreshPromiseRef.current;
  }, []);

  useEffect(() => {
    let unsubscribeProfile = null;

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setAuthError(null);

      // Reset any previous profile listener.
      if (unsubscribeProfile) {
        unsubscribeProfile();
        unsubscribeProfile = null;
      }

      if (firebaseUser) {
        try {
          const profileRef = doc(db, "users", firebaseUser.uid);
          let profileSnap = await getDoc(profileRef);

          // Newly registered users may not have their profile doc written yet.
          // Retry briefly before treating it as missing.
          for (let attempt = 0; attempt < 10 && !profileSnap.exists(); attempt++) {
            await sleep(250);
            profileSnap = await getDoc(profileRef);
          }

          if (profileSnap.exists()) {
            // Enforce centralized entitlement at login/session bootstrap.
            let access = {};
            try {
              const checkAccess = httpsCallable(functions, "get_my_access_status");
              const accessResult = await checkAccess({});
              access = accessResult?.data || {};
              setAccessInfo(access);
            } catch (accessErr) {
              console.error("Failed to validate access status:", accessErr);
              await signOut(auth);
              setUser(null);
              setProfile(null);
              setAuthError("Access verification failed.");
              setLoading(false);
              return;
            }

            const granted = access?.success && access?.canAccess === true;
            setNeedsSubscription(!granted);

            // Keep user signed in even without access — route them to /subscribe
            // so they can self-subscribe via Stripe Checkout (saves 30% app-store fee).
            setUser(firebaseUser);
            setProfile(profileSnap.data());

            // Keep the profile in sync (e.g., coachId assigned shortly after registration).
            unsubscribeProfile = onSnapshot(
              profileRef,
              (snap) => {
                if (snap.exists()) {
                  const nextProfile = snap.data();
                  setProfile((prev) => {
                    // Re-resolve only from source-of-truth fields. Mirrored
                    // entitlement fields are written by backend refreshes and
                    // must not trigger another access check.
                    const accessKeys = [
                      "compAccess",
                      "subscriptionStatus",
                      "subscriptionSource",
                      "coachId",
                      "isCoach",
                      "isAdmin",
                      "coachBillingStatus",
                      "coachSeatsPurchased",
                      "competitionAccessEventIds",
                    ];
                    const changed = accessKeys.some(
                      (k) => prev?.[k] !== nextProfile?.[k]
                    );
                    if (changed) {
                      refreshAccess().catch(() => {});
                    }
                    return nextProfile;
                  });
                }
              },
              (err) => {
                console.error("Failed to subscribe to profile:", err);
              }
            );
          } else {
            console.warn("⚠️ No profile found.");
            await signOut(auth);
            setUser(null);
            setProfile(null);
            setAuthError("No profile found — please contact an admin.");
          }
        } catch (err) {
          console.error("Failed to load profile:", err);
          await signOut(auth);
          setUser(null);
          setProfile(null);
          setAuthError("Failed to load profile.");
        }
      } else {
        setUser(null);
        setProfile(null);
        setNeedsSubscription(false);
        setAccessInfo(null);
      }

      setLoading(false);
    });

    return () => {
      if (unsubscribeProfile) unsubscribeProfile();
      unsubscribe();
    };
  }, [sleep, refreshAccess]);

  const logout = useCallback(async () => {
    try {
      await signOut(auth);
      setUser(null);
      setProfile(null);
      setNeedsSubscription(false);
      setAccessInfo(null);
    } catch (err) {
      console.error("Logout failed:", err);
      throw err;
    }
  }, []);

  const value = useMemo(
    () => ({ user, profile, loading, logout, authError, needsSubscription, accessInfo, refreshAccess }),
    [user, profile, loading, logout, authError, needsSubscription, accessInfo, refreshAccess]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
export function useAuth() {
  return useContext(AuthContext);
}
